import { format } from "date-fns";

export function formarDateTime(date, formatStr = "yyyy-MM-dd hh:mm:ss") {
  return date ? format(date, formatStr) : "";
}

// Build a yyyy-mm-dd string from a Date using its LOCAL calendar parts.
function toLocalISODate(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Append a "Z" (UTC) designator to ISO-like datetime strings that carry a time
// component but no timezone marker. The backend stores instants in UTC but may
// serialize them without the trailing "Z" (e.g. "2026-08-30T18:30:00"), which
// JavaScript would otherwise interpret in the browser's LOCAL timezone.
function ensureUtcDesignator(str) {
  const hasTime = /\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(str);
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(str);
  if (hasTime && !hasZone) {
    return `${str.replace(" ", "T")}Z`;
  }
  return str;
}

// Parse any incoming API date value into a Date, interpreting timezone-less
// datetimes as UTC so they convert correctly into the user's local timezone.
// Returns null for empty or unparseable input.
export function parseApiDateAsUtc(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (Array.isArray(value)) {
    return parseApiDateAsUtc(value[0]);
  }
  const str = String(value).trim();
  // dd/mm/yyyy with an optional time component -> build explicit UTC parts.
  const dmy = str.match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?(?:[.,](\d{1,3}))?)?$/,
  );
  if (dmy) {
    const ms = Number((dmy[7] || "0").padEnd(3, "0"));
    const built = new Date(
      Date.UTC(
        Number(dmy[3]),
        Number(dmy[2]) - 1,
        Number(dmy[1]),
        Number(dmy[4] || 0),
        Number(dmy[5] || 0),
        Number(dmy[6] || 0),
        ms,
      ),
    );
    return Number.isNaN(built.getTime()) ? null : built;
  }
  const parsed = new Date(ensureUtcDesignator(str));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// Convert any incoming date value into a local-calendar yyyy-mm-dd string
// suitable for the date picker. Values stored in UTC (ISO date-time strings or
// Date objects) are shifted into the user's local timezone, while bare
// date-only strings (yyyy-mm-dd or dd/mm/yyyy) are treated as plain calendar
// dates with no timezone shift.
export function utcToLocalDateInput(value) {
  if (!value) return "";
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? "" : toLocalISODate(value);
  }
  if (Array.isArray(value)) {
    return utcToLocalDateInput(value[0]);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    // dd/mm/yyyy -> plain calendar date (already localized upstream)
    const dmy = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
    // date-only yyyy-mm-dd (no time component) -> plain calendar date
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    // ISO date-time -> shift into local timezone. Timezone-less values are
    // treated as UTC by parseApiDateAsUtc.
    const parsed = parseApiDateAsUtc(trimmed);
    if (parsed) return toLocalISODate(parsed);
  }
  return "";
}

// Convert a local-calendar yyyy-mm-dd string (as selected in the UI) into a
// UTC ISO-8601 string for persistence. The date is interpreted at local
// midnight and serialised in UTC so the backend always stores UTC.
export function localDateInputToUtcIso(value) {
  if (!value) return null;
  const m = String(value)
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return value; // not a bare local date; pass through untouched
  const localMidnight = new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    0,
    0,
    0,
    0,
  );
  return Number.isNaN(localMidnight.getTime())
    ? null
    : localMidnight.toISOString();
}

export const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export const numberFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export function decamelize(str, separator) {
  separator = typeof separator === "undefined" ? "_" : separator;
  let asd = str
    .replace(/([a-z\d])([A-Z])/g, "$1" + separator + "$2")
    .replace(/([A-Z]+)([A-Z][a-z\d]+)/g, "$1" + separator + "$2")
    .toLowerCase();

  return asd.charAt(0).toUpperCase() + asd.substr(1, asd.length - 1);
}
